import { useEffect, useState, type ReactNode } from 'react';

export interface EpochBarProps {
  readonly epochLabel: string;
  readonly phase: string;
  /** Progress as 0..100 percent (clamped at render time). */
  readonly progress: number;
  /** Human-readable remaining time (e.g. "17h 33m") or null. */
  readonly remaining: string | null;
  /** UTC end time string (e.g. "11:46 UTC") or null. */
  readonly utcEnd: string | null;
  readonly className?: string;
}

/**
 * Compact epoch progress card extracted from the miniapp EpochCard layout.
 *
 * Layout: epoch label + phase on the left, percent + flipping time
 * on the right, thin progress bar on the bottom.
 *
 * The right side flips between `remaining` and `utcEnd` every 10 seconds
 * via simple state toggle. Click the right side button to toggle manually.
 *
 * If `utcEnd` is null, no flip happens — stays on `remaining` permanently.
 *
 * Width 80px on the flip container + nowrap to keep both forms on a
 * single line (fix from motion-demo-v2 — wider container avoids wrap).
 */
export function EpochBar({
  epochLabel,
  phase,
  progress,
  remaining,
  utcEnd,
  className = '',
}: EpochBarProps): ReactNode {
  const [showUtc, setShowUtc] = useState(false);
  const hasFlip = remaining !== null && utcEnd !== null;

  useEffect(() => {
    if (!hasFlip) return;
    const id = setInterval(() => setShowUtc((v) => !v), 10000);
    return () => clearInterval(id);
  }, [hasFlip]);

  const rightLabel = hasFlip ? (showUtc ? utcEnd : remaining) : remaining;
  const clampedProgress = Math.max(0, Math.min(100, progress));

  return (
    <div
      className={[
        'rounded-xl bg-surface-2 px-4 py-3',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[13px] font-medium tracking-[-0.3px] text-text-primary">
            {epochLabel}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-[1px] text-text-tertiary">
            {phase}
          </span>
        </div>
        <button
          type="button"
          onClick={() => hasFlip && setShowUtc((v) => !v)}
          className="flex items-center gap-2 text-xs"
          aria-label="Toggle epoch time format"
        >
          <span className="font-mono font-medium tabular-nums text-text-primary">
            {clampedProgress.toFixed(0)}%
          </span>
          <div className="relative h-4 w-20 overflow-hidden whitespace-nowrap text-right">
            <span className="font-mono text-[11px] text-text-tertiary">
              {rightLabel ?? ''}
            </span>
          </div>
        </button>
      </div>
      <div className="mt-2 h-[2px] overflow-hidden rounded-full bg-border-strong">
        <div
          data-progress-bar
          className="h-full rounded-full bg-text-primary transition-[width] duration-medium ease-standard"
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </div>
  );
}
